import { Injectable, OnDestroy } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
} from '@angular/common/http';
import { BehaviorSubject, Observable, Subscription, throwError } from 'rxjs';
import { AuthService } from '../services/auth/auth.service';
import { AuthTokens } from '../models/auth-tokens';
import { TokenService } from '../services/token/token.service';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { AuthAnswer } from '../models/auth-answer';
import { IHeaders } from '../models/headers';

@Injectable({
  providedIn: 'root',
  deps: [AuthService, TokenService],
})
export class AuthInterceptor implements HttpInterceptor, OnDestroy {
  private tokens!: AuthTokens | null;
  private tokenSubscribe!: Subscription;
  private isRefreshing = false;
  private appRoles = 'admin,inspector'; // This is provisional because now we have pending the roles validation
  private refreshTokenSubject: BehaviorSubject<string | null> =
    new BehaviorSubject<string | null>(null);

  constructor(
    private authService: AuthService,
    private tokenService: TokenService
  ) {
    this.initialize();
  }

  public initialize() {
    this.tokenSubscribe = this.authService.auth.subscribe((tokens) => {
      this.tokens = tokens;
    });
  }

  ngOnDestroy() {
    this.tokenSubscribe.unsubscribe();
  }

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    const url = request.url;
    const token = this.tokenService.getToken();
    if (token) {
      request = this.addTokenHeader(request, token);
    }
    return next.handle(request).pipe(
      catchError((error) => {
        if (
          error instanceof HttpErrorResponse &&
          error.status === 401 &&
          !url.includes('auth/')
        ) {
          return this.handleRefreshToken(request, next);
        }
        return throwError(error);
      })
    );
  }

  private handleRefreshToken(request: HttpRequest<any>, next: HttpHandler) {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      const refreshToken = this.tokenService.getRefreshToken();

      if (refreshToken)
        return this.authService.refreshToken(refreshToken).pipe(
          switchMap((token: AuthAnswer) => {
            this.isRefreshing = false;

            this.tokenService.saveData(token);
            this.refreshTokenSubject.next(token.accessToken);

            return next.handle(this.addTokenHeader(request, token.accessToken));
          }),
          catchError((err) => {
            this.isRefreshing = false;
            this.authService.logout();
            return throwError(err);
          })
        );
    }

    return this.refreshTokenSubject.pipe(
      filter((token) => token !== null),
      take(1),
      switchMap((token) => next.handle(this.addTokenHeader(request, token)))
    );
  }

  private addTokenHeader(request: HttpRequest<any>, token: string | null) {
    const headers: IHeaders = { Authorization: `Bearer ${token}` };
    if (!request.url.includes('/auth/')) {
      headers.app_roles = this.appRoles;
    }
    return request.clone({ setHeaders: { ...headers } });
  }
}
